// ============================================================
// ContactMismatchNote.tsx — (client-sync) THE one amber note for when the
// CASE's copy of the client's email, phone or address differs from the
// CLIENT RECORD — which bills, credit notes, statements and the consent check
// use. Shown in the case's Client details (straight after a save, or any time
// later), the bill send window and the issue dialog (utils/contactDiff):
//   "This case’s email is x; the client record has y. Bills go to the client
//    record."  [Update the client record] [Use the client record’s email]
// Each button is ONE request (POST /api/cases/:id/contact-sync) carrying the
// values the user was shown: the server compares them with the current case +
// record in one transaction, writes, and returns both sides as they now are —
// so the note, the case page (applyContactSync) and the parent update from the
// response, with no re-read and no reload. On click: "Updating…" on that
// button and both buttons disabled at once.
// A shown value that really changed meanwhile → 409 with both current values
// → "The client record changed meanwhile. Its … is now …; this case has ….
// Choose again." (and the note shows the new values). Only a version bump for
// some other reason (consent recorded, another field saved) is NOT a conflict.
// Self-loading from caseId (fetchCaseById, once), or handed `sides` by a
// parent that already holds them (Client details).
// (Batch 2, M18) Shown as the shared ActionBox (message first, the real
// buttons under it); a failure reads the server's own words (errorText) —
// never "Request failed with status code 400". A successful action announces
// 'clientDetails' (utils/caseChangeEvents) so Settlement re-reads.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react'
import { contactSyncApi, fetchCaseById, isClientRecordChanged, type ContactSyncResult } from '../../api/casevoApi'
import { contactDiffs, contactSidesOfRaw, keepRecordLabel, mismatchMessage, type ContactDiff, type ContactGroup } from '../../utils/contactDiff'
import { useCasevoStore } from '../../store/useCasevoStore'
import { applyRemoteCaseEdit } from '../../hooks/useAutoSave'
import type { Case } from '../../types/case.types'
import ActionBox from './ActionBox'
import { errorText } from '../billing/billingUi'
import { emitCaseChange } from '../../utils/caseChangeEvents'

type Sides = ReturnType<typeof contactSidesOfRaw>
type Direction = 'toRecord' | 'toCase'

// The 409 sentence with both current values (pure; exported for tests).
export const changedMeanwhileMessage = (label: string, recordValue: string, caseValue: string): string =>
  `The client record changed meanwhile. Its ${label} is now ${recordValue}; this case has ${caseValue}. Choose again.`

// A failed action in words (exported for tests): the server's own message
// (errorText); axios's bare "Request failed with status code …" / "Network
// Error" never shows — the plain fallback does.
const FAILED = 'That didn’t work. Please try again.'
export const actionErrorText = (e: unknown): string => {
  const t = errorText(e, FAILED)
  return /^Request failed with status code \d+$|^Network Error$|^timeout of \d+ms exceeded$/.test(t) ? FAILED : t
}

// The case page's store (if this case is open) takes the server's answer as
// a saved edit: the case copy + the client record's values and stamps, the
// autosave baseline advanced — so no reload, and no spurious save or 409 after.
const text = (v: unknown): string => (typeof v === 'string' ? v : '')
// A title starts with a capital ("Email differs…"; the group labels are lower case for mid-sentence use).
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)
export const applyContactSync = (caseId: string, r: Pick<ContactSyncResult, 'case' | 'record' | 'caseUpdatedAt' | 'clientUpdatedAt'>): void => {
  const open = useCasevoStore.getState().currentCase
  if (!open || open.id !== caseId || !r.caseUpdatedAt) return
  const asPerson = (vals: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, k === 'hasEmailAddress' ? (v === true || v === false ? v : null) : text(v)]))
  const updates = {
    clientDetails: { ...open.clientDetails, ...asPerson(r.case) },
    ...(open.client ? { client: { ...open.client, ...asPerson(r.record), ...(r.clientUpdatedAt ? { updatedAt: r.clientUpdatedAt } : {}) } } : {}),
  } as Partial<Case>
  applyRemoteCaseEdit(updates, r.caseUpdatedAt)
}

interface Props {
  caseId: string
  // Already-held sides (Client details) — else they're loaded from caseId.
  sides?: Sides | null
  // Groups not to show here (e.g. ones being edited and not yet saved).
  exclude?: ContactGroup[]
  // Runs before an action (e.g. flush the case's autosave).
  before?: () => Promise<void>
  // After an action: `result` = the server's answer (both sides now); none =
  // a 409 (the parent may re-read). The parent updates anything else it shows.
  onChanged?: (group: ContactGroup, direction: Direction, result?: ContactSyncResult) => void | Promise<void>
  className?: string
}

const ContactMismatchNote: React.FC<Props> = ({ caseId, sides: given, exclude = [], before, onChanged, className = '' }) => {
  const [loaded, setLoaded] = useState<Sides | null>(null)
  const [busy, setBusy] = useState<{ group: ContactGroup; direction: Direction } | null>(null)
  const [notice, setNotice] = useState<Partial<Record<ContactGroup, string>>>({})
  const [done, setDone] = useState<string | null>(null)
  const load = useCallback(async (): Promise<void> => {
    if (given !== undefined) return
    try { setLoaded(contactSidesOfRaw(await fetchCaseById(caseId))) } catch { setLoaded(null) }
  }, [caseId, given])
  useEffect(() => { void load() }, [load])
  const sides = given !== undefined ? given : loaded
  const diffs = sides ? contactDiffs(sides.caseCopy, sides.record).filter((d) => !exclude.includes(d.group)) : []
  if (!diffs.length) return done ? <p role="status" className={`m-0 text-[12.5px] text-casevo-text-soft ${className}`}>{done}</p> : null

  // This note's own copy (self-loading) takes the given values.
  const mergeLocal = (caseVals: Record<string, unknown>, recordVals: Record<string, unknown>, clientUpdatedAt?: string | null): void => {
    if (given !== undefined) return
    setLoaded((s) => (s ? {
      caseCopy: { ...s.caseCopy, ...caseVals },
      record: s.record ? { ...s.record, ...recordVals } : s.record,
      clientUpdatedAt: clientUpdatedAt ?? s.clientUpdatedAt,
    } : s))
  }

  const act = async (d: ContactDiff, direction: Direction): Promise<void> => {
    if (busy) return
    setBusy({ group: d.group, direction }); setNotice((n) => ({ ...n, [d.group]: undefined })); setDone(null)
    try {
      await before?.()
      const r = await contactSyncApi(caseId, { group: d.group, direction, shown: { case: d.caseFields, record: d.recordFields } })
      mergeLocal(r.case, r.record, r.clientUpdatedAt)
      applyContactSync(caseId, r)
      emitCaseChange(caseId, 'clientDetails')
      setDone(direction === 'toRecord' ? `Client record updated: ${d.label} is now ${d.caseValue}.` : `This case now uses the client record’s ${d.label}.`)
      void onChanged?.(d.group, direction, r)
    } catch (e) {
      if (isClientRecordChanged(e)) {
        // Both CURRENT values come back with the 409 — show them.
        const data = (e as { response?: { data?: { data?: { record?: Record<string, unknown>; case?: Record<string, unknown> } } } }).response?.data?.data
        const fresh = data?.record && data.case ? contactDiffs({ ...sides?.caseCopy, ...data.case }, { ...sides?.record, ...data.record }).find((x) => x.group === d.group) : undefined
        if (data?.record && data.case) mergeLocal(data.case, data.record)
        void onChanged?.(d.group, direction)
        setNotice((n) => ({ ...n, [d.group]: fresh ? changedMeanwhileMessage(d.label, fresh.recordValue, fresh.caseValue) : 'The client record changed meanwhile. Check both values and choose again.' }))
      } else {
        setNotice((n) => ({ ...n, [d.group]: actionErrorText(e) }))
      }
    } finally { setBusy(null) }
  }

  return (
    <div className={`grid gap-2 ${className}`}>
      {diffs.map((d) => {
        const mine = busy?.group === d.group
        return (
          <div key={d.group} data-contact-group={d.group}>
            {/* (Audit fix batch 2) The single notice: the fact, then the quiet
                alternative (left) and the main step (right) in its bar. */}
            <ActionBox live ariaLabel={`${cap(d.label)} differs from the client record`} title={`${cap(d.label)} differs from the client record`}
              message={<>
                <span className="block">{mismatchMessage(d)}</span>
                {notice[d.group] && <span role="alert" className="block mt-1 font-semibold text-casevo-danger">{notice[d.group]}</span>}
              </>}
              actions={[
                { label: keepRecordLabel(d), kind: 'link', onClick: () => { void act(d, 'toCase') }, disabled: !!busy, busy: mine && busy?.direction === 'toCase', busyLabel: 'Updating…' },
                { label: 'Update the client record', kind: 'primary', onClick: () => { void act(d, 'toRecord') }, disabled: !!busy, busy: mine && busy?.direction === 'toRecord', busyLabel: 'Updating…' },
              ]} />
          </div>
        )
      })}
    </div>
  )
}

export default ContactMismatchNote
